import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { useT } from '../i18n';
import type { AccessSession, Role, TokenRecord } from '../types/token';

interface ConsoleProps {
  session: AccessSession;
}

export default function Console({ session }: ConsoleProps) {
  const t = useT();
  const [records, setRecords] = useState<TokenRecord[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string>('');

  const [company, setCompany] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [customToken, setCustomToken] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    refresh();
  }, []);

  async function refresh() {
    setStatus('loading');
    try {
      const list = await api.tokens.list();
      setRecords(list);
      setStatus('ready');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load');
      setStatus('error');
    }
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (creating || !company.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await api.tokens.create({
        company: company.trim(),
        role,
        token: customToken.trim() || undefined,
      });
      setRecords((prev) => [created, ...prev]);
      setCompany('');
      setRole('viewer');
      setCustomToken('');
    } catch (err) {
      if (err instanceof ApiError) setCreateError(err.message);
      else setCreateError(err instanceof Error ? err.message : 'create failed');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(target: TokenRecord) {
    if (!window.confirm(t('console.confirmRevoke', { company: target.company }))) return;
    try {
      await api.tokens.revoke(target.token);
      setRecords((prev) => prev.filter((r) => r.token !== target.token));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'revoke failed');
    }
  }

  async function copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      // ignore — user can still select manually
    }
  }

  return (
    <section className="flex flex-col gap-10">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-3">
        <h2 className="text-3xl font-semibold text-cocoa">{t('console.title')}</h2>
        <span className="font-mono text-xs text-coffee/70">{t('console.apiHint')}</span>
      </header>

      <form
        onSubmit={handleCreate}
        className="flex flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-5 shadow-soft transition-shadow duration-200 hover:shadow-float md:p-6"
      >
        <h3 className="font-mono text-sm text-cocoa">{t('console.mintTitle')}</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1.5 sm:col-span-1">
            <span className="text-[10px] font-mono uppercase tracking-wider text-coffee/80">
              {t('console.companyLabel')}
            </span>
            <input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder={t('console.companyPlaceholder')}
              className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-cocoa shadow-sm placeholder:text-neutral-400 transition focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-950/10"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-coffee/80">
              {t('console.roleLabel')}
            </span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm font-mono text-cocoa shadow-sm transition focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-950/10"
            >
              <option value="viewer">viewer</option>
              <option value="admin">admin</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-coffee/80">
              {t('console.customTokenLabel')}
            </span>
            <input
              value={customToken}
              onChange={(e) => setCustomToken(e.target.value)}
              placeholder={t('console.customTokenPlaceholder')}
              className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm font-mono text-cocoa shadow-sm placeholder:text-neutral-400 transition focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-950/10"
            />
          </label>
        </div>
        {createError && (
          <p className="text-cocoa font-mono text-xs italic">{createError}</p>
        )}
        <div>
          <button
            type="submit"
            disabled={creating || !company.trim()}
            className="rounded-full bg-tan px-4 py-2.5 font-mono text-sm text-neutral-100 shadow-soft transition hover:-translate-y-0.5 hover:bg-neutral-800 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {creating ? t('console.creating') : t('console.create')}
          </button>
        </div>
      </form>

      <div className="flex flex-col gap-3">
        <h3 className="font-mono text-sm text-cocoa">{t('console.allTokens')}</h3>
        {status === 'loading' && (
          <p className="text-coffee text-sm">{t('console.loading')}</p>
        )}
        {status === 'error' && (
          <p className="text-cocoa font-mono text-sm italic">
            {t('console.error', { error })}
          </p>
        )}
        {status === 'ready' && (
          <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-soft transition-shadow duration-200 hover:shadow-float">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-[10px] font-mono uppercase tracking-wider text-coffee/80">
                <tr>
                  <th className="px-4 py-3">{t('console.thCompany')}</th>
                  <th className="px-4 py-3">{t('console.thRole')}</th>
                  <th className="px-4 py-3">{t('console.thToken')}</th>
                  <th className="px-4 py-3">{t('console.thCreated')}</th>
                  <th className="px-4 py-3 text-right">{t('console.thActions')}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const isBootstrap = r.protected === true;
                  const isSelf = r.token === session.token;
                  const cantRevoke = isBootstrap || isSelf;
                  const reason = isBootstrap
                    ? t('console.cantRevokeBootstrap')
                    : isSelf
                      ? t('console.cantRevokeSelf')
                      : '';
                  return (
                    <tr key={r.token} className="border-t border-neutral-100 transition hover:bg-neutral-50/80">
                      <td className="px-4 py-3 text-cocoa">{r.company}</td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            r.role === 'admin'
                              ? 'rounded-full bg-neutral-900 px-2 py-0.5 font-mono text-xs text-neutral-100'
                              : 'rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-xs text-coffee'
                          }
                        >
                          {r.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => copyToken(r.token)}
                          title={t('console.copyHint')}
                          className="rounded px-1 font-mono text-xs text-coffee transition hover:bg-neutral-100 hover:text-cocoa focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
                        >
                          {r.token}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-coffee/70">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleRevoke(r)}
                          disabled={cantRevoke}
                          title={reason || t('console.revoke')}
                          className="rounded-full px-2 py-1 font-mono text-xs text-cocoa transition hover:bg-neutral-100 hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15 disabled:cursor-not-allowed disabled:text-neutral-400 disabled:hover:bg-transparent disabled:hover:text-neutral-400"
                        >
                          {t('console.revoke')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {records.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-coffee/70 font-mono text-xs"
                    >
                      {t('console.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
